import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, ShieldCheck, CreditCard, Banknote, Landmark, ArrowLeft } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { formatMoney, computeTotals } from '../utils/money';

const field = {
  width: '100%', padding: '13px 14px', borderRadius: '10px',
  border: '1px solid var(--mist)', fontSize: '0.9rem', fontFamily: 'DM Sans',
  background: 'var(--white)', color: 'var(--charcoal)', outline: 'none',
};
const label = {
  display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--mid)',
  marginBottom: '6px', letterSpacing: '0.02em',
};

const PAYMENT_METHODS = [
  { id: 'stripe', label: 'Card (Stripe)', desc: 'Secure card payment', Icon: CreditCard },
  { id: 'cod', label: 'Cash on Delivery', desc: 'Pay when it arrives', Icon: Banknote },
  { id: 'bank_transfer', label: 'Bank Transfer', desc: 'Pay by bank transfer', Icon: Landmark },
];

export default function CheckoutPage() {
  const { items, clearCart } = useCart();
  const { settings } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();

  const totals = useMemo(() => computeTotals(items, settings), [items, settings]);
  const money = (v) => formatMoney(v, settings);

  const [form, setForm] = useState({
    email: '', fullName: '', phone: '',
    line1: '', line2: '', city: '', postcode: '', country: '',
  });
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [ageOk, setAgeOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        email: user.email || f.email,
        fullName: user.name || f.fullName,
        phone: user.phone || f.phone,
      }));
    }
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSubmit =
    items.length > 0 && form.email && form.fullName && form.line1 && form.city && form.postcode && ageOk;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!canSubmit) {
      setError('Please complete all required fields and confirm your age.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        email: form.email,
        paymentMethod,
        ageConfirmed: ageOk,
        shippingAddress: {
          fullName: form.fullName, phone: form.phone,
          line1: form.line1, line2: form.line2,
          city: form.city, postcode: form.postcode,
          country: form.country || (settings.currency === 'USD' ? 'US' : 'GB'),
        },
        items: items.map((it) => ({
          productId: it.id, name: it.name, price: it.price, qty: it.qty || 1,
          image: it.image, mg: it.mg, isBundle: !!it.isBundle,
        })),
      };

      const res = await api.createOrder(payload);
      const order = res.order || res;

      if (paymentMethod === 'stripe') {
        // Ask the backend for a Stripe Checkout session and redirect to it.
        const session = await api.createStripeSession(order.id || order._id);
        if (session?.url) {
          clearCart();
          window.location.href = session.url;
          return;
        }
      }

      clearCart();
      navigate(`/order/${order.id || order._id}`, { state: { order, email: form.email } });
    } catch (err) {
      if (err.offline) {
        setError('The store server is not reachable yet. Start the backend, then try again.');
      } else {
        setError(err.message || 'Something went wrong placing your order.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div style={{ paddingTop: '120px', textAlign: 'center', minHeight: '70vh', background: 'var(--cream)' }}>
        <h2 style={{ fontFamily: 'Syne', color: 'var(--forest)', marginBottom: '14px' }}>Your cart is empty</h2>
        <Link to="/shop" style={{
          display: 'inline-flex', gap: '8px', alignItems: 'center', background: 'var(--forest)',
          color: '#fff', padding: '13px 26px', borderRadius: '10px', fontFamily: 'Syne', fontWeight: 700,
        }}>Continue shopping</Link>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '68px', minHeight: '100vh', background: 'var(--cream)' }}>
      <div style={{ maxWidth: '1080px', margin: '0 auto', padding: '40px 24px 80px' }}>
        <Link to="/cart" style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--mid)',
          fontSize: '0.85rem', marginBottom: '20px',
        }}><ArrowLeft size={15} /> Back to cart</Link>

        <h1 style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '2rem', color: 'var(--forest)', marginBottom: '30px' }}>
          Checkout
        </h1>

        <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '32px' }} className="checkout-grid">
          {/* Left: details */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
            {!user && (
              <div style={{
                background: 'var(--white)', border: '1px solid var(--mist)', borderRadius: '16px',
                padding: '16px 20px', fontSize: '0.85rem', color: 'var(--mid)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px',
              }}>
                <span>Have an account?</span>
                <Link to="/login" style={{ color: 'var(--forest)', fontWeight: 700 }}>Log in for faster checkout →</Link>
              </div>
            )}

            <Section title="Contact">
              <div>
                <label style={label}>Email *</label>
                <input style={field} type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" required />
              </div>
            </Section>

            <Section title="Shipping address">
              <div style={{ display: 'grid', gap: '14px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={label}>Full name *</label>
                    <input style={field} value={form.fullName} onChange={set('fullName')} required />
                  </div>
                  <div>
                    <label style={label}>Phone</label>
                    <input style={field} value={form.phone} onChange={set('phone')} />
                  </div>
                </div>
                <div>
                  <label style={label}>Address line 1 *</label>
                  <input style={field} value={form.line1} onChange={set('line1')} required />
                </div>
                <div>
                  <label style={label}>Address line 2</label>
                  <input style={field} value={form.line2} onChange={set('line2')} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={label}>City *</label>
                    <input style={field} value={form.city} onChange={set('city')} required />
                  </div>
                  <div>
                    <label style={label}>Postcode / ZIP *</label>
                    <input style={field} value={form.postcode} onChange={set('postcode')} required />
                  </div>
                  <div>
                    <label style={label}>Country</label>
                    <input style={field} value={form.country} onChange={set('country')} placeholder="GB / US" />
                  </div>
                </div>
              </div>
            </Section>

            <Section title="Payment method">
              <div style={{ display: 'grid', gap: '10px' }}>
                {PAYMENT_METHODS.map(({ id, label: pl, desc, Icon }) => (
                  <button
                    type="button"
                    key={id}
                    onClick={() => setPaymentMethod(id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '14px', textAlign: 'left',
                      padding: '14px 16px', borderRadius: '12px', background: 'var(--white)',
                      border: `2px solid ${paymentMethod === id ? 'var(--forest)' : 'var(--mist)'}`,
                      transition: 'border 0.15s',
                    }}
                  >
                    <Icon size={20} color={paymentMethod === id ? 'var(--forest)' : 'var(--mid)'} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--charcoal)' }}>{pl}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--mid)' }}>{desc}</div>
                    </div>
                    <span style={{
                      width: '18px', height: '18px', borderRadius: '50%',
                      border: `2px solid ${paymentMethod === id ? 'var(--forest)' : 'var(--mist)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {paymentMethod === id && <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: 'var(--forest)' }} />}
                    </span>
                  </button>
                ))}
              </div>
            </Section>
          </div>

          {/* Right: summary */}
          <div style={{
            background: 'var(--white)', border: '1px solid var(--mist)', borderRadius: '20px',
            padding: '26px', height: 'fit-content', position: 'sticky', top: '88px',
          }}>
            <h3 style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '1.05rem', color: 'var(--charcoal)', marginBottom: '18px' }}>
              Order Summary
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '18px', maxHeight: '260px', overflowY: 'auto' }}>
              {items.map((it, i) => (
                <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{ width: '46px', height: '46px', borderRadius: '10px', overflow: 'hidden', background: it.gradient, flexShrink: 0 }}>
                    {it.image && <img src={it.image} alt={it.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--charcoal)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--mid)' }}>Qty {it.qty || 1}</div>
                  </div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--forest)' }}>{money(it.price * (it.qty || 1))}</div>
                </div>
              ))}
            </div>

            <Row label="Subtotal" value={money(totals.subtotal)} />
            <Row label="Shipping" value={totals.shipping === 0 ? 'FREE' : money(totals.shipping)} green={totals.shipping === 0} />
            {totals.tax > 0 && <Row label={`Tax (${Math.round(totals.taxRate * 100)}%)`} value={money(totals.tax)} />}
            <div style={{
              borderTop: '1px solid var(--mist)', paddingTop: '14px', marginTop: '10px', marginBottom: '18px',
              display: 'flex', justifyContent: 'space-between', fontFamily: 'Syne', fontWeight: 700, fontSize: '1.1rem',
            }}>
              <span>Total</span><span style={{ color: 'var(--forest)' }}>{money(totals.total)}</span>
            </div>

            <label style={{
              display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '0.78rem',
              color: 'var(--mid)', marginBottom: '16px', cursor: 'pointer',
            }}>
              <input type="checkbox" checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)} style={{ marginTop: '2px' }} />
              <span>I confirm I am {settings.ageRequirement || 18} years or older and accept the terms of sale.</span>
            </label>

            {error && (
              <div style={{ background: '#fdecec', color: '#c0392b', fontSize: '0.8rem', padding: '10px 12px', borderRadius: '10px', marginBottom: '14px' }}>
                {error}
              </div>
            )}

            <motion.button
              type="submit"
              disabled={!canSubmit || submitting}
              whileTap={{ scale: 0.98 }}
              style={{
                width: '100%', padding: '15px', borderRadius: '12px',
                background: canSubmit && !submitting ? 'var(--forest)' : 'var(--mist)',
                color: '#fff', fontFamily: 'Syne', fontWeight: 700, fontSize: '0.9rem',
                letterSpacing: '0.04em', textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
              }}
            >
              <Lock size={15} />
              {submitting ? 'Placing order…' : `Pay ${money(totals.total)}`}
            </motion.button>

            <p style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--mid)', marginTop: '12px', display: 'flex', gap: '5px', justifyContent: 'center', alignItems: 'center' }}>
              <ShieldCheck size={12} /> Encrypted & age-verified checkout
            </p>
          </div>
        </form>
      </div>

      <style>{`@media (max-width: 820px){ .checkout-grid{ grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div style={{ background: 'var(--white)', border: '1px solid var(--mist)', borderRadius: '16px', padding: '22px' }}>
    <h3 style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '1rem', color: 'var(--charcoal)', marginBottom: '16px' }}>{title}</h3>
    {children}
  </div>
);

const Row = ({ label: l, value, green }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: green ? 'var(--accent-green)' : 'var(--mid)', marginBottom: '8px', fontWeight: green ? 600 : 400 }}>
    <span>{l}</span><span>{value}</span>
  </div>
);
